import type { DataRouter } from "react-router";

const RELOAD_KEY = "lazy-route-reload";

const importers: Array<() => Promise<unknown>> = [];

/**
 * A route's `lazy`: its module downloads on first visit, or earlier through
 * `prefetchLazyRoutes`.
 */
export function lazyRoute<T>(importer: () => Promise<{ route: T }>) {
  importers.push(importer);

  return () =>
    importer().then(
      (module) => {
        sessionStorage.removeItem(RELOAD_KEY);
        return module.route;
      },
      (error) => {
        // A tab opened before a deploy asks for chunk hashes that no longer
        // exist; one reload picks up the new build. Offline (the POS keeps
        // selling without network) a reload would lose the app, so it throws.
        if (navigator.onLine && !sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
          return new Promise<never>(() => {});
        }
        throw error;
      },
    );
}

/**
 * Downloads every lazy route in the background once the first page has
 * rendered, so later navigations only wait on their data.
 */
export function prefetchLazyRoutes(router: DataRouter) {
  const prefetch = () => {
    const whenIdle = window.requestIdleCallback ?? ((run) => setTimeout(run, 200));
    whenIdle(() => importers.forEach((importer) => importer().catch(() => {})));
  };

  const isSettled = () =>
    router.state.initialized && router.state.navigation.state === "idle";

  if (isSettled()) return prefetch();
  const unsubscribe = router.subscribe(() => {
    if (!isSettled()) return;
    unsubscribe();
    prefetch();
  });
}
